import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Select, Switch,
  Popconfirm, Tooltip, App as AntApp,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { transportApi, TrainSchedule, TrainCreatePayload } from '../../api/transport';

const TRAIN_TYPES = [
  { value: 'passenger', label: 'Пассажирский', color: 'blue' },
  { value: 'fast', label: 'Скорый', color: 'orange' },
  { value: 'suburban', label: 'Пригородный', color: 'green' },
];

const DAYS = [
  { value: '1', label: 'Пн' },
  { value: '2', label: 'Вт' },
  { value: '3', label: 'Ср' },
  { value: '4', label: 'Чт' },
  { value: '5', label: 'Пт' },
  { value: '6', label: 'Сб' },
  { value: '7', label: 'Вс' },
];

const POPULAR_CITIES = [
  'Аксай', 'Уральск', 'Актобе', 'Алматы', 'Астана', 'Атырау',
  'Москва', 'Оренбург', 'Илецк', 'Саратов', 'Самара', 'Петропавловск',
  'Шымкент', 'Мангистау (Актау)',
];

const TrainsTab: React.FC = () => {
  const { message } = AntApp.useApp();
  const [trains, setTrains] = useState<TrainSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TrainSchedule | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await transportApi.listTrains();
      setTrains(r.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить поезда');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      train_type: 'passenger',
      days_of_week: ['1', '2', '3', '4', '5', '6', '7'],
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (t: TrainSchedule) => {
    setEditing(t);
    form.setFieldsValue({
      ...t,
      days_of_week: (t.days_of_week || '').split(',').filter(Boolean),
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload: TrainCreatePayload = {
        ...values,
        days_of_week: Array.isArray(values.days_of_week)
          ? values.days_of_week.join(',')
          : values.days_of_week,
      };
      setSubmitting(true);
      if (editing) {
        await transportApi.updateTrain(editing.id, payload);
        message.success('Поезд обновлён');
      } else {
        await transportApi.createTrain(payload);
        message.success('Поезд добавлен');
      }
      setModalOpen(false);
      fetch();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await transportApi.deleteTrain(id);
      message.success('Удалено');
      fetch();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const columns: ColumnsType<TrainSchedule> = [
    {
      title: '№',
      dataIndex: 'train_number',
      key: 'train_number',
      width: 90,
      render: (n) => <Tag>{n}</Tag>,
    },
    {
      title: 'Маршрут',
      dataIndex: 'route_name_ru',
      key: 'route_name_ru',
      render: (v, t) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v}</div>
          <div style={{ fontSize: 11, color: 'var(--l-faint)' }}>
            {t.from_station} → {t.to_station}
          </div>
        </div>
      ),
    },
    {
      title: 'Отпр.',
      dataIndex: 'departure_time',
      key: 'departure_time',
      width: 80,
      render: (v) => <b>{v}</b>,
    },
    {
      title: 'Приб.',
      dataIndex: 'arrival_time',
      key: 'arrival_time',
      width: 80,
      render: (v) => v || '—',
    },
    {
      title: 'Дни',
      dataIndex: 'days_of_week',
      key: 'days_of_week',
      width: 180,
      render: (v: string) => {
        if (!v) return '—';
        if (v.split(',').length === 7) return <Tag>Ежедневно</Tag>;
        const names: Record<string, string> = { '1': 'Пн', '2': 'Вт', '3': 'Ср', '4': 'Чт', '5': 'Пт', '6': 'Сб', '7': 'Вс' };
        return v.split(',').map((d) => names[d]).filter(Boolean).join(', ');
      },
    },
    {
      title: 'Тип',
      dataIndex: 'train_type',
      key: 'train_type',
      width: 130,
      render: (v: string) => {
        const info = TRAIN_TYPES.find((x) => x.value === v);
        return <Tag>{info?.label || v}</Tag>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (a: boolean) => a ? <Tag color="green">Активен</Tag> : <Tag color="default">Скрыт</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 110,
      render: (_, t) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(t)} />
          </Tooltip>
          <Popconfirm title="Удалить поезд?" onConfirm={() => remove(t.id)} okText="Удалить" cancelText="Отмена" okButtonProps={{ danger: true }}>
            <Tooltip title="Удалить"><Button type="text" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--l-fill)' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Добавить поезд
        </Button>
      </div>
      <Table<TrainSchedule>
        rowKey="id"
        columns={columns}
        dataSource={trains}
        loading={loading}
        pagination={{ pageSize: 20, showTotal: (t) => `Всего: ${t}` }}
        locale={{ emptyText: 'Поезда не найдены. Добавьте первый.' }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={editing ? 'Редактировать поезд' : 'Добавить поезд'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        confirmLoading={submitting}
        okText={editing ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Space.Compact block>
            <Form.Item name="train_number" label="Номер поезда" rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
              <Input placeholder="083Ш" />
            </Form.Item>
            <Form.Item name="train_type" label="Тип" style={{ width: 200 }}>
              <Select options={TRAIN_TYPES.map((t) => ({ value: t.value, label: t.label }))} />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="route_name_ru" label="Маршрут (рус.)" rules={[{ required: true }]}>
            <Input placeholder="Уральск — Мангистау" />
          </Form.Item>
          <Form.Item name="route_name_kk" label="Маршрут (каз.)">
            <Input placeholder="Орал — Маңғыстау" />
          </Form.Item>
          <Space.Compact block>
            <Form.Item name="from_station" label="Откуда" rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
              <Select showSearch placeholder="Уральск" options={POPULAR_CITIES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
            <Form.Item name="to_station" label="Куда" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select showSearch placeholder="Мангистау" options={POPULAR_CITIES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
          </Space.Compact>
          <Space.Compact block>
            <Form.Item name="departure_time" label="Время отправления" rules={[{ required: true, pattern: /^\d{2}:\d{2}$/, message: 'Формат HH:MM' }]} style={{ flex: 1, marginRight: 8 }}>
              <Input placeholder="18:45" />
            </Form.Item>
            <Form.Item name="arrival_time" label="Время прибытия" style={{ flex: 1 }} rules={[{ pattern: /^\d{2}:\d{2}$/, message: 'Формат HH:MM' }]}>
              <Input placeholder="18:30" />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="days_of_week" label="Дни курсирования" rules={[{ required: true, message: 'Выберите хотя бы один день' }]}>
            <Select mode="multiple" options={DAYS} placeholder="Пн, Ср, Пт" />
          </Form.Item>
          <Form.Item name="note_ru" label="Примечание (рус.)">
            <Input placeholder="через день" />
          </Form.Item>
          <Form.Item name="is_active" label="Активен" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Да" unCheckedChildren="Нет" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TrainsTab;
